import GetZbooniPopover from "app/components/get-zbooni-popover";
import Sprite from "app/components/sprite";
import { useAnimation } from "app/hooks/useAnimation";
import { useI18nLanguage } from "app/hooks/i18n";
import americanExpress from "app/images/section-images/features/american-express.png";
import applePay from "app/images/section-images/features/apple-pay.png";
import arabicManageRefunds from "app/images/section-images/features/arabic/manage-refunds.png";
import arabicOrderTroubleShooting from "app/images/section-images/features/arabic/order-trouble-shooting.png";
import arabicStartSelling from "app/images/section-images/features/arabic/start-selling.png";
import googlePay from "app/images/section-images/features/google-pay.png";
import manageRefunds from "app/images/section-images/features/manage-refunds.png";
import mastercard from "app/images/section-images/features/mastercard.png";
import orderTroubleShooting from "app/images/section-images/features/order-trouble-shooting.png";
import startSelling from "app/images/section-images/features/start-selling.png";
import visa from "app/images/section-images/features/visa.png";
import { useRef, useState } from "react";
import { Trans } from "react-i18next";

const smallImageCss = `
  animate__animated mb-[10px] max-w-[66px] rounded-2xl opacity-0
  shadow-card-small md:mb-3.5 md:max-w-[63px] lg:max-w-[73px] xl:max-w-[83px]
`;

const largeImageCss = `
  animate__animated mb-[10px] max-w-[120px] opacity-0 shadow-card-md
  md:mb-3.5 md:max-w-[125px] lg:max-w-[130px] xl:max-w-[168px]
`;

const SectionHeader = ({ header }) => (
  <>
    <h2
      className="animate__animated mt-[46px] max-w-[692px]
      text-[24px] font-bold leading-[34px] opacity-0"
      ref={header}
      data-animation="animate__fadeInDown custom__animate__delay-3s"
    >
      <Trans>
        Whether you’re already accepting online payments or not, Zbooni’s got
        you covered in both cases!
      </Trans>
    </h2>
    <div className="decoration-short-round-line mt-[26px]"></div>
  </>
);

const TextContent = ({ title, body, cta, setPopover }) => (
  <div className="relative z-[2]">
    <div
      className="mt-[46px] max-w-xs md:pt-16 lg:mt-0
      lg:max-w-md lg:pt-[114px] xl:max-w-none"
    >
      <h3
        className="animate__animated text-[22px] font-extrabold
        leading-[26px] text-primary opacity-0"
        ref={title}
        data-animation="animate__fadeInDown custom__animate__delay-3s"
      >
        <Trans>Need help with accepting online payments?</Trans>
      </h3>
      <p
        className="animate__animated mt-4 max-w-[500px] font-medium
        leading-[24px] text-primary opacity-0"
        ref={body}
        data-animation="animate__fadeInDown custom__animate__delay-3s"
      >
        <Trans>
          We can make your life easier with payments powered by Zbooni. Start
          accepting all major payment methods and ensure the highest rate of
          successful transaction. Only available in UAE, KSA, Egypt and Jordan.
        </Trans>
      </p>
      <button
        id="cta_acceptPayments_paymentsPoweredByZbooni_downloadTheApp"
        className="dark-green-gradient-btn animate__animated
        mt-[26px] opacity-0 lg:mt-9"
        onClick={setPopover}
        ref={cta}
        data-animation="animate__fadeInDown custom__animate__delay-3s"
      >
        <p>
          <Trans>Download the app</Trans>
        </p>
      </button>
    </div>
  </div>
);

const Shapes = ({
  largeCard1,
  largeCard2,
  largeCard3,
  smallCard1,
  smallCard2,
  smallCard3,
  smallCard4,
  smallCard5,
  vector,
}) => {
  const { locale: language } = useI18nLanguage();
  return (
    <div
      className="relative mt-[60px] h-full w-full md:ml-[35px]
      md:mt-16 lg:mx-[10%] lg:mt-0 xl:ml-[28%]"
    >
      <Sprite
        svgRef={vector}
        dataAnimation="animate__fadeIn custom__animate__delay-3s"
        name="teal-ellipse"
        className="animate__animated absolute top-0 z-[1]
        h-[350px] w-full md:h-[350px] md:w-[350px] lg:h-[370px]
        lg:w-[370px] xl:h-[450px] xl:w-[450px] rtl:xl:left-44"
      />
      <div
        className="relative top-0 z-[2] md:absolute
        lg:left-10 ltr:xl:left-[55px] rtl:xl:left-32"
      >
        <div className="flex flex-row">
          <div className="relative pr-2.5 md:pr-[18px]">
            <div
              className="translate-y-[144px]
              transform md:translate-y-[172px]"
            >
              <div className="flex w-full flex-row justify-end">
                <img
                  src={visa}
                  alt="visa"
                  className={smallImageCss}
                  ref={smallCard1}
                  data-animation="animate__fadeIn animate__delay-1s"
                />
              </div>
              <img
                src={
                  language == "ar"
                    ? arabicOrderTroubleShooting
                    : orderTroubleShooting
                }
                alt="order-trouble-shooting"
                className={largeImageCss}
                ref={largeCard1}
                data-animation="animate__fadeInUp custom__animate__delay-4s"
              />
            </div>
          </div>
          <div className="relative pr-2.5 md:pr-[18px]">
            <div
              className="translate-y-[78px]
              transform md:translate-y-[114px]"
            >
              <img
                src={mastercard}
                alt="mastercard"
                className={smallImageCss}
                ref={smallCard2}
                data-animation="animate__fadeIn animate__delay-1s"
              />
              <img
                src={americanExpress}
                alt="americanExpress"
                className={smallImageCss}
                ref={smallCard3}
                data-animation="animate__fadeIn animate__delay-1s"
              />
              <img
                src={googlePay}
                alt="google-pay"
                className={smallImageCss}
                ref={smallCard4}
                data-animation="animate__fadeIn animate__delay-1s"
              />
              <img
                src={applePay}
                alt="apple-pay"
                className={smallImageCss}
                ref={smallCard5}
                data-animation="animate__fadeIn animate__delay-1s"
              />
            </div>
          </div>
          <div className="relative pr-2.5 md:pr-[18px]">
            <div
              className="translate-y-[34px]
              transform md:translate-y-[-7px]"
            >
              <img
                src={language == "ar" ? arabicManageRefunds : manageRefunds}
                alt="manage-refunds"
                className={largeImageCss}
                ref={largeCard2}
                data-animation="animate__fadeInUp custom__animate__delay-5s"
              />
              <img
                src={language == "ar" ? arabicStartSelling : startSelling}
                alt="start-selling"
                className={largeImageCss}
                ref={largeCard3}
                data-animation="animate__fadeInUp custom__animate__delay-6s"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const logoWrappingOffset = "md:px-0 px-3";

const Logos = () => {
  return (
    <>
      <div className={logoWrappingOffset}>
        <Sprite
          name="payments-samsung-pay"
          className="my-[30px] h-[28px] w-[62px] md:h-[25px] md:w-[55px]"
        />
      </div>
      <div className={logoWrappingOffset}>
        <Sprite
          name="payments-cs10-mada"
          className="my-[30px] h-[28px] w-[84px] md:h-[25px] md:w-[75px]"
        />
      </div>
      <div className={logoWrappingOffset}>
        <Sprite
          name="payments-stc-pay"
          className="my-[30px] h-[28px] w-[95px] md:h-[25px] md:w-[85px]"
        />
      </div>
      <div className={logoWrappingOffset}>
        <Sprite
          name="payments-fawry-pay"
          className="my-[30px] h-[28px] w-[57px] md:h-[25px] md:w-[51px]"
        />
      </div>
      <div className={logoWrappingOffset}>
        <Sprite
          name="payments-orange-money"
          className="my-[30px] h-[28px] w-[104px] md:h-[25px] md:w-[93px]"
        />
      </div>
      <div className={logoWrappingOffset}>
        <Sprite
          name="payments-meeza"
          className="my-[30px] h-[28px] w-[32px] md:h-[25px] md:w-[38px]"
        />
      </div>
      <div className={logoWrappingOffset}>
        <Sprite
          name="payments-vodafone"
          className="my-[30px] h-[28px] w-[114px] md:h-[25px] md:w-[100px]"
        />
      </div>
      <div className={logoWrappingOffset}>
        <Sprite
          name="payments-valu"
          className="my-[30px] h-[28px] w-[65px] md:h-[25px] md:w-[58px]"
        />
      </div>
    </>
  );
};

export const PaymentsPoweredByZbooniSection = () => {
  const [popover, setPopover] = useState(false);
  const header = useRef(null);
  const title = useRef(null);
  const body = useRef(null);
  const cta = useRef(null);
  const largeCard1 = useRef(null);
  const largeCard2 = useRef(null);
  const largeCard3 = useRef(null);
  const smallCard1 = useRef(null);
  const smallCard2 = useRef(null);
  const smallCard3 = useRef(null);
  const smallCard4 = useRef(null);
  const smallCard5 = useRef(null);
  const vector = useRef(null);

  useAnimation([
    header,
    title,
    body,
    cta,
    largeCard1,
    largeCard2,
    largeCard3,
    smallCard1,
    smallCard2,
    smallCard3,
    smallCard4,
    smallCard5,
    vector,
  ]);
  return (
    <section className="relative w-full overflow-hidden">
      <GetZbooniPopover popover={popover} setPopover={setPopover} />
      <div className="zbooni-layout">
        <SectionHeader header={header} />
        <div className="mb-24 flex flex-col md:mb-[100px] md:flex-row lg:mb-28">
          <TextContent
            title={title}
            body={body}
            cta={cta}
            setPopover={() => setPopover(true)}
          />
          <div className="relative z-[1] flex-1">
            <Shapes
              vector={vector}
              largeCard1={largeCard1}
              largeCard2={largeCard2}
              largeCard3={largeCard3}
              smallCard1={smallCard1}
              smallCard2={smallCard2}
              smallCard3={smallCard3}
              smallCard4={smallCard4}
              smallCard5={smallCard5}
            />
          </div>
        </div>
        <div
          className="relative mb-14 flex w-full max-w-[732px]
          flex-row flex-wrap md:mb-8 md:justify-between lg:mb-20"
        >
          <Logos />
        </div>
      </div>
    </section>
  );
};
